import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { NgSelectComponent } from "@ng-select/ng-select";
import { NavController, NavParams, ViewController } from "ionic-angular";
import {
  DropdownItem,
  Order,
  OrderComponent,
  OrderVisitFrequency,
} from "models/order-management/models";
import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { Subject } from "rxjs";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "discipline",
  templateUrl: "discipline.html",
})
export class DisciplineComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() get orderFormState(): OrderFormState {
    return this._orderFormState;
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * ViewChild                                                                *
   ****************************************************************************/
  @ViewChild("hiddenInput") hiddenInput: {
    setFocus: () => void;
  };
  @ViewChild("otherDisciplinesDropdown")
  otherDisciplinesDropdown: NgSelectComponent;

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  dropdownItems: DropdownItem[] = [];
  itemsQuery$ = new Subject<string>();
  selectedItem: string;
  otherDisciplines: DropdownItem[] = null;
  isOtherSelected = false;
  otherDisciplineItemsQuery$ = new Subject<string>();
  selectedOtherDiscipline = null;
  errorText: string | undefined;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService
  ) {}

  private _orderFormState: OrderFormState;

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this.itemsSearch();
    this.checkIsValid();
    const updateObject: OrderCreateRequestUpdate = { updates: {} };
    const componentField = this.orderComponent.properties[0].name;
    updateObject.updates[componentField] = this.selectedItem;
    this.orderCreateRequestEvent.emit(updateObject);
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  get isDisabled() {
    if (this.dropdownItems.length == 1) {
      this.selectedItem = this.dropdownItems[0].value;
      return true;
    }
    return false;
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  set orderFormState(value: OrderFormState) {
    this._orderFormState = value;
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  onItemSelected(event: string) {
    if (event) {
      const updateObject: OrderCreateRequestUpdate = { updates: {} };
      const componentField = this.orderComponent.properties[0].name;
      switch (componentField) {
        case "discipline":
          this.isOtherSelected = event.toUpperCase() === "OTHER";
          if (!this.isOtherSelected) {
            this.selectedOtherDiscipline = null;
          }
          this.orderFormStateEvent.emit({
            discipline: event,
          });
          updateObject.updates[componentField] = event;
          break;
        default:
          updateObject.updates[componentField] = event;
          break;
      }
      this.orderCreateRequestEvent.emit(updateObject);
    }
    this.checkIsValid();
  }

  itemsSearch() {
    this.dropdownItems = this.orderComponent.properties[0].dropdownItems.filter(
      (item) => !item.filterValues
    );
    this.otherDisciplines =
      this.orderComponent.properties[0].dropdownItems.filter(
        (item) => item.filterValues
      );
  }

  checkIsValid() {
    if (this._orderFormState.vfOrders.some(this.disciplineCompare)) {
      // Warn if VF order with the same discipline
      this.errorText =
        "*Please note: A visit frequency order for this discipline already exists.";
    } else {
      this.errorText = undefined;
    }
    const disciplinesDropdown =
      !!this.selectedItem && this.selectedItem.length > 0;
    const otherDisciplinesDropdown =
      !this.isOtherSelected ||
      (!!this.selectedOtherDiscipline &&
        this.selectedOtherDiscipline.trim().length > 0);
    const isValid =
      disciplinesDropdown &&
      (this.isOtherSelected ? otherDisciplinesDropdown : true);
    this.isValidEvent.emit(isValid);
  }

  disciplineCompare = (order: StatefulOrderResponse) => {
    // Disciplines overlap if they are the same and:
    // - not declined
    // - not a PRN only order
    if (
      order.discipline &&
      !order.declined &&
      !OrderVisitFrequency.isPRNOnlyOrder(this._orderFormState)
    ) {
      const currentDiscipline = this.isOtherSelected
        ? this.selectedOtherDiscipline
        : this.selectedItem;
      if (currentDiscipline) {
        return (
          order.discipline.toLocaleLowerCase() ===
          currentDiscipline.toLocaleLowerCase()
        );
      }
    }
    return false;
  };

  isRequired() {
    return (
      this.orderComponent.properties[0].required ||
      !this.isDisabled ||
      this.isOtherSelected
    );
  }

  onItemClear(_event) {
    this.selectedItem = "";
    const componentField = this.orderComponent.properties[0].name;
    const updateObject: OrderCreateRequestUpdate = { updates: {} };

    this.isOtherSelected = false;
    this.selectedOtherDiscipline = "";

    updateObject.updates[componentField] = "";
    this.orderCreateRequestEvent.emit(updateObject);
    this.checkIsValid();
  }

  addDiscipline = (discipline: string) => {
    const componentField = this.orderComponent.properties[0].name;
    const newDiscipline = { label: discipline, value: discipline };

    this.otherDisciplines = [
      ...this.otherDisciplines,
      {
        label: discipline,
        value: discipline,
        filterValues: ["other"],
      },
    ];

    this.selectedOtherDiscipline = newDiscipline.value;
    this.orderFormStateEvent.emit({
      discipline: discipline,
    });

    const updateObject: OrderCreateRequestUpdate = { updates: {} };
    updateObject.updates[componentField] = discipline;
    this.orderCreateRequestEvent.emit(updateObject);

    this.checkIsValid();
  };

  onOtherDisciplineSelected(event: string) {
    if (event) {
      const updateObject: OrderCreateRequestUpdate = { updates: {} };
      const componentField = this.orderComponent.properties[0].name;
      if (event) {
        this.orderFormStateEvent.emit({
          discipline: event,
        });
        updateObject.updates[componentField] = event;
        this.orderCreateRequestEvent.emit(updateObject);
      }
    }
    this.checkIsValid();
  }

  // Hackish fix to show keyboard on IOS / Safari
  onClick() {
    this.hiddenInput.setFocus();
    this.otherDisciplinesDropdown.filterInput.nativeElement.focus();
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "") // Trim leading and trailing whitespace
        .replace(/([a-z])([A-Z])/g, "$1-$2") // Add hyphen between camelCase words
        .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
        .replace(/[^\w-]+/g, "") // Remove punctuation (non-word characters except hyphen)
        .toLowerCase(); // Convert to lowercase
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/discipline/discipline.ts